"use client";

import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Form } from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { FeedbackType, submitFeedback } from "@/utils/feedbackService";
import { AttachmentUpload } from "./feedback/AttachmentUpload";
import { FeedbackFormFields, feedbackFormSchema, FeedbackFormValues } from "./feedback/FeedbackFormFields";
import { SubmitButton } from "./feedback/SubmitButton";
import { uploadAttachment } from "@/utils/fileUploadHelper";
import { z } from "zod";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import type { PageContext } from "@/hooks/usePageContext";

/**
 * Everything we can capture about where/when the feedback was submitted,
 * stored in feedback_logs.user_metadata. This context is what makes feedback
 * actionable downstream (triage, agent prompts), so capture liberally.
 */
function buildUserMetadata(pageContext: PageContext | null): Record<string, any> {
  return {
    browser: navigator.userAgent,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    commit: typeof __APP_COMMIT__ !== "undefined" ? __APP_COMMIT__ : "unknown",
    route: pageContext?.pathname ?? null,
    facility_id: pageContext?.facilityId ?? null,
    facility_name: pageContext?.facilityName ?? null,
    module: pageContext?.module ?? null,
    module_label: pageContext?.moduleLabel ?? null,
    record: pageContext?.record
      ? { type: pageContext.record.type, id: pageContext.record.id, table: pageContext.record.table ?? null }
      : null,
  };
}

const feedbackTypes = [
  { value: "Bug", label: "Bug Report" },
  { value: "Feature", label: "Feature Request" },
  { value: "Improvement", label: "Improvement Suggestion" },
  { value: "General", label: "General Feedback" }
];

interface FeedbackFormProps {
  onSuccess?: () => void;
  currentUser: { id: string } | null;
  defaultFeedbackType?: FeedbackType;
  /** Where the user was when they opened the feedback modal, if known. */
  pageContext?: PageContext | null;
}

const FeedbackForm = ({ onSuccess, currentUser, defaultFeedbackType = "General", pageContext = null }: FeedbackFormProps) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [attachmentPreview, setAttachmentPreview] = useState<string | null>(null);

  const initialValues: FeedbackFormValues = {
    feedbackType: defaultFeedbackType,
    description: "",
    consoleErrors: "",
  };

  const form = useForm<z.infer<typeof feedbackFormSchema>>({
    resolver: zodResolver(feedbackFormSchema),
    defaultValues: initialValues,
  });

  const onSubmit = async (values: FeedbackFormValues) => {
    if (!currentUser) {
      toast({
        variant: "destructive",
        title: "Authentication Required",
        description: "You must be logged in to submit feedback.",
      });
      return;
    }

    try {
      setIsSubmitting(true);
      
      let attachmentUrl = null;

      if (attachmentFile) {
        attachmentUrl = await uploadAttachment(attachmentFile);
      }
      
      const result = await submitFeedback({
        userId: currentUser.id,
        feedbackType: values.feedbackType as FeedbackType,
        description: values.description,
        consoleErrors: values.feedbackType === "Bug" ? values.consoleErrors : undefined,
        attachmentUrl: attachmentUrl,
        module: pageContext?.module ?? null,
        userMetadata: buildUserMetadata(pageContext),
      });
      
      if (!result.success) {
        throw new Error(result.message);
      }

      toast({
        title: "Feedback Submitted",
        description: "Thank you for your feedback! We'll review it shortly.",
      });
      
      form.reset(initialValues);
      setAttachmentFile(null);
      setAttachmentPreview(null);
      
      if (onSuccess) {
        onSuccess();
      }
      
    } catch (error) {
      console.error("Error submitting feedback:", error);
      
      toast({
        variant: "destructive",
        title: "Submission Failed",
        description: error instanceof Error ? error.message : "An unexpected error occurred.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!currentUser) {
    return (
      <Alert variant="destructive" className="mb-6">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Authentication Required</AlertTitle>
        <AlertDescription>
          You need to be logged in to submit feedback. Please sign in and try again.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="animate-fade-in">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <FeedbackFormFields 
            form={form} 
            feedbackTypes={feedbackTypes} 
          />
          
          <div className="space-y-2">
            <div className="text-primary block">Attachment (Optional)</div>
            <AttachmentUpload 
              attachmentFile={attachmentFile}
              setAttachmentFile={setAttachmentFile}
              attachmentPreview={attachmentPreview}
              setAttachmentPreview={setAttachmentPreview}
            />
          </div>
          
          <SubmitButton isSubmitting={isSubmitting} />
        </form>
      </Form>
    </div>
  );
};

export default FeedbackForm;
